import React from 'react';
import { Table, Tag, Button, Space, Tooltip, Empty, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { 
  EditOutlined, 
  DeleteOutlined, 
  StarOutlined, 
  StarFilled,
  PhoneOutlined,
  MailOutlined,
  UserOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ProducerContact } from '../../../types/contact';
import { formatPhone } from '../../../utils/phone';

const { Text } = Typography;

interface ContactListProps {
  contacts: ProducerContact[];
  onEdit: (contactId: string) => void;
  onDelete: (contactId: string) => void;
  onToggleFavorite: (contactId: string) => void;
  onRefresh: () => void;
}

/**
 * ContactList Component
 * 
 * Renders a table of producer contacts with proper ordering:
 * - Main contact first
 * - Favorite contacts second
 * - Other contacts ordered by modification date
 * 
 * Features:
 * - Displays empty state when no contacts
 * - Shows contact information in a compact table format
 * - Provides inline actions for edit, delete, and favorite toggle
 */
export const ContactList: React.FC<ContactListProps> = ({
  contacts,
  onEdit,
  onDelete,
  onToggleFavorite,
}) => {
  const { t } = useTranslation();

  // Contacts are already sorted by the backend:
  // ORDER BY is_main_contact DESC, is_favorite DESC, modified DESC
  const sortedContacts = [...contacts].sort((a, b) => {
    // Main contact first
    if (a.is_main_contact !== b.is_main_contact) {
      return a.is_main_contact ? -1 : 1;
    }
    
    // Then favorites
    if (a.is_favorite !== b.is_favorite) {
      return a.is_favorite ? -1 : 1;
    }
    
    // Then by modification date (most recent first)
    return new Date(b.modified).getTime() - new Date(a.modified).getTime();
  });

  const columns: ColumnsType<ProducerContact> = [
    {
      title: t('contacts.table.favorite') || 'Fav',
      key: 'favorite',
      width: 60,
      align: 'center',
      render: (_, record) => {
        const isOnlyContact = contacts.length === 1;
        const isFavorite = record.is_favorite;
        const cannotUnmark = isOnlyContact && isFavorite;
        
        return (
          <Tooltip 
            title={cannotUnmark 
              ? (t('contacts.error.cannot_unmark_only_favorite') || 'No se puede desmarcar el favorito si es el único contacto')
              : (isFavorite ? (t('contacts.unmark_favorite') || 'Desmarcar favorito') : (t('contacts.mark_favorite') || 'Marcar como favorito'))
            }
          >
            <Button
              type="text"
              size="small"
              icon={isFavorite ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
              onClick={() => onToggleFavorite(record.id)}
              disabled={cannotUnmark}
            />
          </Tooltip>
        );
      },
    },
    {
      title: t('contacts.table.name') || 'Nombre',
      key: 'name',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Space size={8}>
            <Text strong>{record.contact.name} {record.contact.surname}</Text>
            {record.is_main_contact && (
              <Tag color="blue">{t('contacts.main') || 'Principal'}</Tag>
            )}
          </Space>
        </Space>
      ),
    },
    {
      title: t('contacts.table.position') || 'Cargo',
      key: 'position',
      width: 180,
      render: (_, record) => (
        record.position ? (
          <Space size={4}>
            <UserOutlined style={{ color: '#8c8c8c' }} />
            <Text>{record.position.name}</Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: t('contacts.table.phone') || 'Teléfono',
      key: 'phone',
      width: 200,
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          {record.contact.main_phone && (
            <Space size={4}>
              <PhoneOutlined style={{ color: '#8c8c8c' }} />
              <Text>{formatPhone(record.contact.main_phone)}</Text>
            </Space>
          )}
          {record.contact.secondary_phone && (
            <Space size={4}>
              <PhoneOutlined style={{ color: '#8c8c8c' }} />
              <Text type="secondary">{formatPhone(record.contact.secondary_phone)}</Text>
            </Space>
          )}
          {!record.contact.main_phone && !record.contact.secondary_phone && (
            <Text type="secondary">-</Text>
          )}
        </Space>
      ),
    },
    {
      title: t('contacts.table.email') || 'Email',
      key: 'email',
      width: 220,
      render: (_, record) => (
        record.contact.email ? (
          <Space size={4}>
            <MailOutlined style={{ color: '#8c8c8c' }} />
            <Text>{record.contact.email}</Text>
          </Space>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: t('contacts.table.notes') || 'Notas',
      key: 'notes',
      ellipsis: {
        showTitle: false,
      },
      render: (_, record) => (
        record.notes ? (
          <Tooltip title={record.notes}>
            <Text type="secondary" ellipsis>{record.notes}</Text>
          </Tooltip>
        ) : (
          <Text type="secondary">-</Text>
        )
      ),
    },
    {
      title: t('common.actions') || 'Acciones',
      key: 'actions',
      width: 120,
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        const isLastContact = contacts.length === 1;
        const deleteTooltip = isLastContact 
          ? (t('contacts.error.cannot_delete_last') || 'No se puede eliminar el último contacto')
          : (t('common.delete') || 'Eliminar');
        
        return (
          <Space size={4}>
            <Tooltip title={t('common.edit') || 'Editar'}>
              <Button
                type="text"
                size="small"
                icon={<EditOutlined />}
                onClick={() => onEdit(record.id)}
              />
            </Tooltip>
            <Tooltip title={deleteTooltip}>
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record.id)}
                disabled={isLastContact}
              />
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  // Empty state
  if (contacts.length === 0) {
    return (
      <Empty
        description={
          <Text type="secondary">
            {t('contacts.empty') || 'No hay contactos asociados a este productor'}
          </Text>
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        style={{ padding: '40px 0' }}
      />
    );
  }

  return (
    <Table
      columns={columns}
      dataSource={sortedContacts}
      rowKey="id"
      pagination={false}
      size="middle"
      scroll={{ x: 1200 }}
    />
  );
};

export default ContactList;
